import type { ComponentType } from 'react';
import { useFlowObserverPanelStore } from '../store/flowObserverPanelStore.js';
import type {
  FlowObserverPanelTabDescriptor,
  FlowObserverTabContentProps,
  FlowObserverConstraints,
  TabEngineState,
} from '../types/panel.types.js';
import type { ExecutionLogEntry } from '../types/tab-data.types.js';
import {
  filterExecutionLogEntries,
  summarizeExecutionLogEntries,
  readOutputDataFromLastExecution,
  readLastKnownData,
  readNodeConfig,
  type LogFilterCriteria,
  type LogSummary,
} from '../query/logQuery.js';

const FlowObserverPanelEngine = {
  configure(constraints: Partial<FlowObserverConstraints>): void {
    useFlowObserverPanelStore.getState().configure(constraints);
  },

  setDataBufferMaxSize(size: number): void {
    // Clamp size between 100-10000
    const clampedSize = Math.max(100, Math.min(size, 10000));
    try {
      localStorage.setItem('flowObserver.dataBufferMaxSize', clampedSize.toString());
    } catch (e) {
      console.warn('Failed to save buffer size to localStorage', e);
    }
    // Update the store
    useFlowObserverPanelStore.getState().configure({ dataBufferMaxSize: clampedSize });
  },

  getDataBufferMaxSize(): number {
    try {
      const stored = localStorage.getItem('flowObserver.dataBufferMaxSize');
      if (stored) {
        return parseInt(stored, 10);
      }
    } catch (e) {
      console.warn('Failed to read buffer size from localStorage', e);
    }
    return 2000; // Default
  },

  registerTab(
    descriptor: FlowObserverPanelTabDescriptor,
    contentComponent: ComponentType<FlowObserverTabContentProps>
  ): void {
    useFlowObserverPanelStore.getState().registerTab(descriptor, contentComponent);
  },

  unregisterTab(id: string): void {
    useFlowObserverPanelStore.getState().unregisterTab(id);
  },

  setTabEngineState(id: string, state: TabEngineState): void {
    useFlowObserverPanelStore.getState().setTabEngineState(id, state);
  },

  setTabBadge(id: string, badge: number | string | undefined): void {
    useFlowObserverPanelStore.getState().setTabBadge(id, badge);
  },

  activateTab(id: string): void {
    useFlowObserverPanelStore.getState().activateTab(id);
  },

  /** Sets userState='open' without checking engineState. For init-time restoration. */
  forceOpenTab(id: string): void {
    useFlowObserverPanelStore.getState().forceOpenTab(id);
  },

  pushData<T = unknown>(id: string, data: T): void {
    useFlowObserverPanelStore.getState().pushData(id, data);
  },

  clearData(id: string): void {
    useFlowObserverPanelStore.getState().clearData(id);
  },

  /** Independent/filtered log query — the "query interface" every panel can call into, not just Execution Logs. */
  queryLogs(tabId: string, criteria?: LogFilterCriteria): ExecutionLogEntry[] {
    const buffer = (useFlowObserverPanelStore.getState().tabRuntimeStates[tabId]?.dataBuffer ?? []) as ExecutionLogEntry[];
    return criteria ? filterExecutionLogEntries(buffer, criteria) : buffer;
  },

  queryLogsSummary(tabId: string, criteria?: LogFilterCriteria): LogSummary {
    return summarizeExecutionLogEntries(FlowObserverPanelEngine.queryLogs(tabId, criteria));
  },

  /** nodeKey-only convenience over the live execution-logs buffer; see logQuery.ts for the pure/testable form. */
  readOutputDataFromLastExecution(nodeKey: string): { outputData: unknown; entry: ExecutionLogEntry } | null {
    return readOutputDataFromLastExecution(FlowObserverPanelEngine.queryLogs('execution-logs'), nodeKey);
  },

  readLastKnownData(nodeKey: string): { initiated?: ExecutionLogEntry; completed?: ExecutionLogEntry } {
    return readLastKnownData(FlowObserverPanelEngine.queryLogs('execution-logs'), nodeKey);
  },

  readNodeConfig(nodeKey: string): Record<string, unknown> | undefined {
    return readNodeConfig(FlowObserverPanelEngine.queryLogs('execution-logs'), nodeKey);
  },

  /** True once the user has manually picked a tab this session — see §5.1 nav-jump fix. */
  hasUserSelectedTab(): boolean {
    return useFlowObserverPanelStore.getState().hasUserSelectedTab;
  },

  markUserSelectedTab(): void {
    useFlowObserverPanelStore.getState().markUserSelectedTab();
  },
};

export { FlowObserverPanelEngine };
